import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SlotArt, LiveDot } from './BattleParts';
import { C, fmtMulti, isThirdPlace, multiColor, roundLabel, sidesOf, totalRounds } from './battleLib';

// ============================================
// Tournament bracket. The whole tree exists from the moment the bracket is
// rolled (winners of matches 2i and 2i+1 meet in the next round), so it can
// draw real connector lines. Positions are computed, not flexed: a round-1
// match sits on a fixed row, every later match sits halfway between the two
// it's fed by. Drag / wheel / pinch to explore, click a match to open it.
// ============================================

export const CARD_W = 280;
const ROW_H = 44;
// two rows + the divider + a 1px border top and bottom
export const CARD_H = ROW_H * 2 + 3;
const COL_GAP = 64;
const ROW_GAP = 16;
const HEAD_H = 36;
// The page opens on the whole bracket whenever it fits at least this big;
// below it (32+ seats, phones) the live match is zoomed to instead.
const READABLE_SCALE = 0.7;
// Gap between the final and the 3rd-place card under it (room for its label).
const THIRD_GAP = 58;

export function bracketLayout(size, thirdPlace = false) {
  const rounds = totalRounds(size);
  const pos = new Map(); // `${round}:${idx}` -> { x, y }
  for (let r = 1; r <= rounds; r++) {
    const count = size / 2 ** r;
    for (let i = 0; i < count; i++) {
      const x = (r - 1) * (CARD_W + COL_GAP);
      const y = r === 1
        ? HEAD_H + i * (CARD_H + ROW_GAP)
        : (pos.get(`${r - 1}:${2 * i}`).y + pos.get(`${r - 1}:${2 * i + 1}`).y) / 2;
      pos.set(`${r}:${i}`, { x, y });
    }
  }
  const width = rounds * CARD_W + (rounds - 1) * COL_GAP;
  let height = HEAD_H + (size / 2) * (CARD_H + ROW_GAP) - ROW_GAP;
  // The 3rd-place match sits under the final (same round, idx 1). No lines
  // run to it — its players are the semis' losers, not their winners.
  if (thirdPlace) {
    const final = pos.get(`${rounds}:0`);
    const third = { x: final.x, y: final.y + CARD_H + THIRD_GAP };
    pos.set(`${rounds}:1`, third);
    height = Math.max(height, third.y + CARD_H);
  }
  return { rounds, pos, width, height };
}

function SeatRow({ play, won, lost, placeholder, focus }) {
  if (!play) {
    return (
      <div style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: 12, color: 'rgba(161,161,170,0.5)' }}>
        {placeholder}
      </div>
    );
  }
  return (
    <div style={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px', opacity: lost ? 0.4 : 1, transition: 'opacity 300ms' }}>
      <SlotArt slot={play.slot} width={29} radius={4} />
      <div style={{ minWidth: 0, flex: 1, lineHeight: 1.2 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: won ? 800 : 600, color: won ? '#fff' : 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {play.name}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{play.slot?.name || play.slot?.raw || '—'}</span>
      </div>
      {play.multi != null && (
        <span style={{ fontSize: 13, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: multiColor(play.multi) }}>{fmtMulti(play.multi)}</span>
      )}
      {won && <span aria-label="winner" style={{ width: 3, height: 16, borderRadius: 2, background: C.violet, boxShadow: '0 0 8px rgba(168,85,247,0.9)', flexShrink: 0 }} />}
      {focus && play.multi == null && <LiveDot size={6} />}
    </div>
  );
}

function BracketMatch({ match, x, y, focused, current, onSelect, feeders }) {
  const { a, b } = sidesOf(match);
  const done = match.status === 'done';
  const [hover, setHover] = useState(false);
  const border = focused ? C.violet : hover && onSelect ? 'rgba(168,85,247,0.55)' : current && !done ? 'rgba(255,255,255,0.2)' : C.line;
  return (
    <button
      type="button"
      data-match={match.id}
      onClick={onSelect ? () => onSelect(match) : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', left: x, top: y, width: CARD_W, height: CARD_H, padding: 0, overflow: 'hidden', textAlign: 'left',
        borderRadius: 16, border: `1px solid ${border}`, background: focused ? 'linear-gradient(135deg, rgba(168,85,247,0.22), rgba(12,8,20,0.95))' : current ? '#0d0a14' : 'rgba(12,10,18,0.85)',
        boxShadow: focused ? '0 0 30px -6px rgba(168,85,247,0.8)' : 'none', cursor: onSelect ? 'pointer' : 'default',
        pointerEvents: onSelect ? 'auto' : 'none', fontFamily: C.font, transition: 'border-color 200ms',
      }}
    >
      <SeatRow play={a[0]} won={done && match.winner_side === 'a'} lost={done && match.winner_side === 'b'} placeholder={feeders?.[0]} focus={focused} />
      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
      <SeatRow play={b[0]} won={done && match.winner_side === 'b'} lost={done && match.winner_side === 'a'} placeholder={feeders?.[1]} focus={focused} />
    </button>
  );
}

function BracketCanvas({ state, onSelect, layout }) {
  const { battle, matches } = state;
  const size = battle.bracket_size;
  const byKey = useMemo(() => new Map(matches.map((m) => [`${m.round}:${m.idx}`, m])), [matches]);

  const lines = [];
  for (let r = 2; r <= layout.rounds; r++) {
    const count = size / 2 ** r;
    for (let i = 0; i < count; i++) {
      const to = layout.pos.get(`${r}:${i}`);
      for (const child of [2 * i, 2 * i + 1]) {
        const from = layout.pos.get(`${r - 1}:${child}`);
        const cm = byKey.get(`${r - 1}:${child}`);
        const x1 = from.x + CARD_W;
        const y1 = from.y + CARD_H / 2;
        const x2 = to.x;
        // land on the row the winner will occupy (top row for even feeders)
        const y2 = to.y + 1 + (child % 2 === 0 ? ROW_H / 2 : ROW_H + 1 + ROW_H / 2);
        const xm = x1 + COL_GAP / 2;
        lines.push(
          <path
            key={`${r}-${i}-${child}`}
            d={`M${x1} ${y1} H${xm} V${y2} H${x2}`}
            fill="none"
            stroke={cm?.status === 'done' ? 'rgba(192,132,252,0.75)' : 'rgba(168,85,247,0.18)'}
            strokeWidth={cm?.status === 'done' ? 2 : 1} strokeDasharray={cm?.status === 'done' ? undefined : '4 5'}
          />
        );
      }
    }
  }

  const head = { position: 'absolute', top: 0, width: CARD_W, fontFamily: C.display, fontSize: 12, fontWeight: 700, letterSpacing: '0.3px', textAlign: 'center' };
  return (
    <div style={{ position: 'relative', width: layout.width, height: layout.height }}>
      {Array.from({ length: layout.rounds }, (_, k) => k + 1).map((r) => (
        <div key={r} style={{ ...head, left: (r - 1) * (CARD_W + COL_GAP), color: battle.status === 'live' && battle.current_round === r ? C.lav : C.dim }}>
          {roundLabel(r, size)}
          {battle.status === 'live' && battle.picking_round === r && <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.7)' }}>· picking</span>}
        </div>
      ))}
      <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} width={layout.width} height={layout.height} aria-hidden>
        {lines}
      </svg>
      {matches.map((m) => {
        const p = layout.pos.get(`${m.round}:${m.idx}`);
        if (!p) return null;
        const third = isThirdPlace(m, battle);
        const prev = m.round > 1 ? roundLabel(m.round - 1, size).toLowerCase() : '';
        const feeders = third
          ? [`loser of ${prev} #1`, `loser of ${prev} #2`]
          : m.round > 1
            ? [`winner of ${prev} #${2 * m.idx + 1}`, `winner of ${prev} #${2 * m.idx + 2}`]
            : ['waiting for the roll', 'waiting for the roll'];
        return (
          <Fragment key={m.id}>
            {third && <div style={{ ...head, left: p.x, top: p.y - 22, color: C.dim }}>Bronze match</div>}
            <BracketMatch
              match={m}
              x={p.x}
              y={p.y}
              focused={battle.focus_match_id === m.id && battle.status === 'live'}
              current={battle.current_round === m.round}
              onSelect={onSelect}
              feeders={feeders}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

const clamp = (s) => Math.min(1.8, Math.max(0.15, s));

// Wheel needs a non-passive listener (React's onWheel is passive, so
// preventDefault would be ignored and the page would scroll instead).
export default function BracketView({ state, onSelect, height = 560 }) {
  const { bracket_size: size, third_place: third } = state.battle;
  const layout = useMemo(() => bracketLayout(size, third), [size, third]);
  const box = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, s: 1 });
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const moved = useRef(false);

  // The whole bracket, centred and scaled to fit (never above 1:1).
  const fitView = useCallback(() => {
    const el = box.current;
    if (!el) return null;
    const pad = 24;
    const top = 56; // clear of the zoom buttons
    const s = clamp(Math.min((el.clientWidth - pad * 2) / layout.width, (el.clientHeight - top - pad) / layout.height, 1));
    return { s, x: (el.clientWidth - layout.width * s) / 2, y: top + (el.clientHeight - top - pad - layout.height * s) / 2 };
  }, [layout]);
  const fit = useCallback(() => { const f = fitView(); if (f) setView(f); }, [fitView]);

  // Zoom to one match. Reads matches through a ref so a feed push (chat
  // entries land about once a second) doesn't count as a reason to move.
  const matchesRef = useRef(state.matches);
  matchesRef.current = state.matches;
  const focusTo = useCallback((matchId) => {
    const m = matchesRef.current.find((x) => x.id === matchId);
    const el = box.current;
    const p = m && layout.pos.get(`${m.round}:${m.idx}`);
    if (!p || !el) return;
    setView((v) => {
      const s = clamp(Math.max(0.8, v.s));
      return { s, x: el.clientWidth / 2 - (p.x + CARD_W / 2) * s, y: el.clientHeight / 2 - (p.y + CARD_H / 2) * s };
    });
  }, [layout]);

  // On load and whenever a different match goes on stream: the whole bracket
  // if it's readable that way, else straight to the live match.
  const focusId = state.battle.status === 'live' ? state.battle.focus_match_id : null;
  useLayoutEffect(() => {
    const f = fitView();
    if (!f) return;
    if (focusId && f.s < READABLE_SCALE) focusTo(focusId);
    else setView(f);
  }, [focusId, fitView, focusTo]);

  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setView((v) => {
        const s = clamp(v.s * (e.deltaY < 0 ? 1.12 : 1 / 1.12));
        return { s, x: mx - ((mx - v.x) / v.s) * s, y: my - ((my - v.y) / v.s) * s };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;
    gesture.current = null;
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const prev = pointers.current.get(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      if (Math.abs(dx) + Math.abs(dy) > 0) {
        if (!moved.current && Math.hypot(dx, dy) < 3) return;
        moved.current = true;
        // capture only once it's really a drag, so a plain click still
        // reaches the match card underneath
        if (box.current?.setPointerCapture) box.current.setPointerCapture(e.pointerId);
        setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
      }
    } else if (pts.length === 2) {
      moved.current = true;
      const [p1, p2] = pts;
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const rect = box.current.getBoundingClientRect();
      const mx = (p1.x + p2.x) / 2 - rect.left;
      const my = (p1.y + p2.y) / 2 - rect.top;
      if (gesture.current) {
        const g = gesture.current;
        const ratio = dist / g.dist;
        setView((v) => {
          const s = clamp(v.s * ratio);
          return { s, x: mx - ((g.mx - v.x) / v.s) * s, y: my - ((g.my - v.y) / v.s) * s };
        });
      }
      gesture.current = { dist, mx, my };
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const zoom = (f) => {
    const el = box.current;
    const mx = el.clientWidth / 2;
    const my = el.clientHeight / 2;
    setView((v) => {
      const s = clamp(v.s * f);
      return { s, x: mx - ((mx - v.x) / v.s) * s, y: my - ((my - v.y) / v.s) * s };
    });
  };

  const ctl = {
    height: 32, minWidth: 32, borderRadius: 9, border: `1px solid ${C.line}`, background: 'rgba(14,12,20,0.9)', color: C.soft,
    fontSize: '0.9rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.font, padding: '0 8px',
  };
  return (
    <div
      ref={box}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={(e) => { if (moved.current) { e.stopPropagation(); e.preventDefault(); } }}
      style={{
        position: 'relative', height, overflow: 'hidden', borderRadius: 16, border: `1px solid ${C.line}`, cursor: 'grab', touchAction: 'none', userSelect: 'none',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(168,85,247,0.12), transparent 60%), rgba(5,5,7,0.6)',
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`, width: layout.width, height: layout.height }}>
        <BracketCanvas state={state} onSelect={onSelect} layout={layout} />
      </div>
      <div style={{ position: 'absolute', right: 12, top: 12, display: 'flex', gap: 6 }} onPointerDown={(e) => e.stopPropagation()}>
        {[['＋', () => zoom(1.25), 'Zoom in'], ['－', () => zoom(0.8), 'Zoom out'], ['⤢', fit, 'Fit the whole bracket']].map(([t, fn, title]) => (
          <button key={t} type="button" title={title} onClick={fn} style={ctl}>{t}</button>
        ))}
        {focusId && (
          <button type="button" onClick={() => focusTo(focusId)} style={{ ...ctl, fontSize: '0.62rem', letterSpacing: '1.2px', textTransform: 'uppercase', color: C.lav }}>
            live match
          </button>
        )}
      </div>
      <span style={{ position: 'absolute', left: 12, bottom: 10, pointerEvents: 'none', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(161,161,170,0.6)' }}>
        drag to explore · scroll or pinch to zoom · tap a match
      </span>
    </div>
  );
}
